/**
 * Services grid — what a pet owner can do in the app, in friendly labels.
 * Clay tiles with a bouncy lift on hover.
 *
 * Mostly the vendor module taxonomy (see features/onboarding/types.ts), with one
 * deliberate exception: Adoption is peer-to-peer, so it is something an owner can
 * do here but NOT a module a vendor signs up for. Don't re-derive this list from
 * ALL_MODULE_TYPES.
 */
import {
  Bone,
  Coffee,
  Dog,
  FlaskConical,
  Flame,
  GraduationCap,
  Heart,
  Pill,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';

type Category = {
  icon: LucideIcon;
  label: string;
  blurb: string;
  tint: string;
};

const CATEGORIES: Category[] = [
  { icon: Stethoscope, label: 'Vets & clinics', blurb: 'Check-ups & care', tint: 'bg-info text-info-foreground' },
  { icon: Dog, label: 'Dog walkers', blurb: 'Daily strolls', tint: 'bg-primary text-primary-foreground' },
  { icon: GraduationCap, label: 'Trainers', blurb: 'Good-boy school', tint: 'bg-success text-success-foreground' },
  { icon: Coffee, label: 'Pet cafés', blurb: 'Treat-friendly spots', tint: 'bg-warning text-warning-foreground' },
  { icon: Bone, label: 'Food & treats', blurb: 'Delivered fresh', tint: 'bg-accent text-accent-foreground' },
  { icon: Pill, label: 'Pharmacy', blurb: 'Meds on demand', tint: 'bg-info text-info-foreground' },
  { icon: FlaskConical, label: 'Labs', blurb: 'Tests & results', tint: 'bg-primary text-primary-foreground' },
  { icon: Heart, label: 'Adoption', blurb: 'Find a forever friend', tint: 'bg-success text-success-foreground' },
  { icon: Flame, label: 'Memorial care', blurb: 'Gentle goodbyes', tint: 'bg-muted text-muted-foreground' },
];

export function LandingCategories() {
  return (
    <section id="services" className="scroll-mt-24 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Everything your pet needs, under one roof
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            From wellness to wagging tails — book any of these from a single, friendly app.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3">
          {CATEGORIES.map(({ icon: Icon, label, blurb, tint }) => (
            <div
              key={label}
              className="clay-press group flex items-center gap-4 rounded-card border-2 border-brand-outline-variant/20 bg-card p-5 shadow-clay transition-all duration-normal ease-bounce hover:-translate-y-1 hover:shadow-clay-lg"
            >
              <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-button shadow-clay-sm transition-transform duration-normal ease-bounce group-hover:scale-110 ${tint}`}
              >
                <Icon className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <p className="font-heading text-base font-bold leading-tight">{label}</p>
                <p className="truncate text-sm text-muted-foreground">{blurb}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
